import type { PaginaDeSector } from "../sectores"
import { PAGINAS_DE_SECTOR } from "../sectores"

/**
 * The English version of the sector pages (`/en/for/[sector]`). Same entries,
 * same order and the same modules as `PAGINAS_DE_SECTOR`; only the text and
 * the links change.
 */
export const PAGINAS_DE_SECTOR_EN: PaginaDeSector[] = [
  {
    slug: "hospitality",
    frase: "From the supplier order to closing the till.",
    nombre: "Hospitality",
    tipo: PAGINAS_DE_SECTOR[0].tipo,
    modulos: PAGINAS_DE_SECTOR[0].modulos,
    metaTitulo: "Software for hospitality: shifts, front of house, till and recipe costing · Zindra",
    metaDescripcion:
      "Shifts and clock-in, menu and table orders, kitchen and bar, stock, recipe costing and closing the till for bars, restaurants and cafés. Turn on only what you use.",
    antetitulo: "FOR BARS, RESTAURANTS, CAFÉS AND BAKERIES",
    titular: "From the supplier order to closing the till, in one place.",
    entradilla:
      "Team shifts and clock-in, menu and table orders, kitchen and bar, stock, recipe costing and the till. What a restaurant uses, already set up, and whatever you don’t use comes off.",
    llamada: "Set up Zindra for my restaurant",
    segunda: { texto: "Start free with the digital menu", href: "/en/digital-menu" },
    diaTitulo: "A day in your restaurant",
    dia: [
      { hora: "08:30", titulo: "The supplier order arrives", texto: "It’s checked against what was ordered, goes into stock, and its price updates the cost of every recipe that uses it.", modulos: ["compras", "inventario"] },
      { hora: "10:00", titulo: "The morning shift starts", texto: "Everyone clocks in on the tablet at the bar. You see who’s missing and how it compares with the week’s rota.", modulos: ["fichaje", "turnos"] },
      { hora: "11:30", titulo: "The kitchen preps", texto: "Prep comes from the recipe with its quantities, and the opening checklist gets ticked off.", modulos: ["producciones", "tareas"] },
      { hora: "13:30", titulo: "Lunch service", texto: "Guests order from the menu at the table or the waiter takes the order. Each dish shows up on the kitchen or bar screen.", modulos: ["cartas_pedidos", "reservas"] },
      { hora: "23:45", titulo: "Closing the till", texto: "The day’s receipts, the totals by payment method and the close, reconciled later with the bank.", modulos: ["tickets", "banca"] },
      { hora: "Monday", titulo: "You look at the week", texto: "Sales, staff cost and margin per dish on one dashboard. Or ask the agent in the chat.", modulos: ["informes", "chat"] },
    ],
    ejemplo: { titulo: "A 12-person restaurant, with everything", personas: PAGINAS_DE_SECTOR[0].ejemplo.personas },
    especial: {
      etiqueta: "FREE, FOREVER",
      titulo: "Start with your digital menu",
      texto: "Your menu with a QR code and your listing in the restaurant guide, at no cost. When you want table orders, kitchen or shifts, they switch on over the same menu.",
      llamada: "Create my free menu",
      href: "/en/digital-menu",
    },
    integracionesTitulo: "Square and your delivery stay",
    integraciones: [
      { nombre: "Square", texto: "Sales and receipts from Square flow into the till and reports on their own." },
      { nombre: "Glovo", texto: "Delivery orders, alongside the rest of the day’s sales." },
      { nombre: "Your bank", texto: "Transactions via Norma 43, to reconcile the close and expenses." },
    ],
    matices: [
      { titulo: "Recipes, not products", texto: "Production talks about recipes, prep and recipe costing." },
      { titulo: "Split shifts", texto: "Morning and evening on the same day, across several venues." },
      { titulo: "Tables and dining rooms", texto: "Tables only appear when you turn on Front of house and menus." },
    ],
    preguntas: [
      { pregunta: "Do I have to change my POS?", respuesta: "Not if you use Square: Zindra reads its sales and receipts. It’s the only POS that connects today." },
      { pregunta: "Can I start with just shifts and clock-in?", respuesta: "Yes. Turn them on on their own and add front of house, stock or the till later, on the same data." },
      { pregunta: "Does it work for several venues?", respuesta: "Yes. Each venue is a location, with its own team, stock and till." },
      { pregunta: "Is the digital menu really free?", respuesta: "Yes: the menu and the guide listing are free. You only pay for the modules you turn on." },
    ],
    cierre: "Your restaurant, set up in an afternoon.",
  },
  {
    slug: "retail",
    frase: "Stock, till and team for every shop.",
    nombre: "Retail",
    tipo: PAGINAS_DE_SECTOR[1].tipo,
    modulos: PAGINAS_DE_SECTOR[1].modulos,
    metaTitulo: "Software for shops: stock, till and shifts · Zindra",
    metaDescripcion: "Stock per shop, supplier purchases, till and closing, team shifts and clock-in. For retailers with one shop or several.",
    antetitulo: "FOR BRICK-AND-MORTAR RETAIL, ONE SHOP OR SEVERAL",
    titular: "Stock, till and team for every shop, without spreadsheets.",
    entradilla: "What’s in each shop, what sold today and who’s working tomorrow. With supplier orders and closing the till in the same place.",
    llamada: "Set up Zindra for my shop",
    segunda: { texto: "See the modules", href: "/en/modules" },
    diaTitulo: "A day in your shop",
    dia: [
      { hora: "09:30", titulo: "Opening and clocking in", texto: "The team clocks in on the tablet at the counter and follows the opening checklist.", modulos: ["fichaje", "tareas"] },
      { hora: "11:00", titulo: "The order arrives", texto: "It’s checked against what was ordered and goes into that shop’s stock.", modulos: ["compras", "inventario"] },
      { hora: "All day", titulo: "Selling", texto: "Square sales are recorded with their receipts and take stock off.", modulos: ["tickets"] },
      { hora: "20:30", titulo: "Closing the till", texto: "The totals by payment method and the day’s summary, reconciled later with the bank.", modulos: ["tickets", "banca"] },
      { hora: "Thursday", titulo: "The rota goes out", texto: "Next week’s shifts, on everyone’s phone.", modulos: ["turnos", "ausencias"] },
    ],
    ejemplo: { titulo: "A shop with 6 people", personas: PAGINAS_DE_SECTOR[1].ejemplo.personas },
    especial: {
      etiqueta: "SEVERAL SHOPS",
      titulo: "Each shop is a location",
      texto: "With its own team, stock and till. You see them separately or all together, and the price doesn’t go up for having more shops.",
      llamada: "Set up Zindra for my shops",
      href: "/en/pricing?sector=tienda",
    },
    integracionesTitulo: "Square and your bank",
    integraciones: [
      { nombre: "Square", texto: "Square sales and receipts, every day." },
      { nombre: "Your bank", texto: "Transactions via Norma 43 or GoCardless." },
      { nombre: "Shopify", texto: "If you also sell online, the same stock." },
    ],
    matices: [
      { titulo: "Stock per shop", texto: "Each shop with its own stockroom." },
      { titulo: "Opening and closing", texto: "Tasks per shift, with who did them." },
      { titulo: "No tables or kitchen", texto: "You don’t see the hospitality features if you don’t use them." },
    ],
    preguntas: [
      { pregunta: "Do I have to change my POS?", respuesta: "Not if you use Square: Zindra reads its sales. It’s the only POS that connects today." },
      { pregunta: "Does it work if I also sell online?", respuesta: "Yes. With Shopify, the shop and the website share the same stock." },
      { pregunta: "And with several shops?", respuesta: "Each shop is a location; the price doesn’t go up for having more locations." },
    ],
    cierre: "Your shop, set up in an afternoon.",
  },
  {
    slug: "ecommerce",
    frase: "From the warehouse to the shipped order, with Shopify.",
    nombre: "Ecommerce",
    tipo: PAGINAS_DE_SECTOR[2].tipo,
    modulos: PAGINAS_DE_SECTOR[2].modulos,
    metaTitulo: "Software for ecommerce: stock, production and margin per channel · Zindra",
    metaDescripcion: "Stock, purchases with shipping and duties, batch production and the warehouse team, connected to Shopify and NetSuite. And the margin of each channel at real cost.",
    antetitulo: "FOR ONLINE STORES AND BRANDS THAT MAKE THEIR OWN PRODUCTS",
    titular: "From the warehouse to the shipped order, with the cost of every product in view.",
    entradilla: "Stock, supplier purchases, production and the warehouse team, connected to Shopify. And the margin of each channel at real cost, not the one on the product sheet.",
    llamada: "Set up Zindra for my online store",
    segunda: { texto: "See the integrations", href: "/en/integrations" },
    diaTitulo: "A day in your warehouse",
    dia: [
      { hora: "08:00", titulo: "The goods arrive", texto: "They’re checked against the order; their price, with shipping and duties, updates the cost of every product.", modulos: ["compras", "inventario"] },
      { hora: "09:00", titulo: "Batch production", texto: "Production orders from the recipe, with what’s consumed and what comes out.", modulos: ["producciones"] },
      { hora: "All day", titulo: "The team clocks in by station", texto: "Who’s on picking, who’s on shipping, and how much goes out per hour.", modulos: ["fichaje", "workforce"] },
      { hora: "Every order", titulo: "The Shopify sale comes in", texto: "The order takes stock off and is ready to invoice.", modulos: ["inventario", "facturacion"] },
      { hora: "Monday", titulo: "You check margin per channel", texto: "Online store, marketplaces, distribution: what each one earns at true cost.", modulos: ["informes", "chat"] },
    ],
    ejemplo: { titulo: "A brand with 10 people", personas: PAGINAS_DE_SECTOR[2].ejemplo.personas },
    especial: {
      etiqueta: "INSIDE SHOPIFY",
      titulo: "Zindra also lives in your Shopify admin",
      texto: "It installs as a Shopify app and links to your company. Orders, products and customers come in on their own.",
      llamada: "See integrations",
      href: "/en/integrations",
    },
    integracionesTitulo: "Your store and your logistics",
    integraciones: [
      { nombre: "Shopify", texto: "Orders, products and customers." },
      { nombre: "NetSuite", texto: "Invoices, recipe costing, orders and purchase costs." },
      { nombre: "ShipHero", texto: "Your warehouse’s stock levels, movements, suppliers and purchase orders." },
    ],
    matices: [
      { titulo: "Batches, not recipes", texto: "Production talks about batches and orders." },
      { titulo: "Cost with everything in", texto: "Includes shipping and duties from the last purchase." },
      { titulo: "Margin per channel", texto: "Online, marketplaces, B2B and distribution, separately." },
    ],
    preguntas: [
      { pregunta: "Does it replace Shopify?", respuesta: "No. Shopify keeps selling; Zindra handles stock, production, the team and the numbers." },
      { pregunta: "What if I use NetSuite?", respuesta: "Zindra reads customers, invoices, recipe costing, production orders and costs from NetSuite." },
      { pregunta: "Can I start with just inventory?", respuesta: "Yes. Everything else is added on the same stock." },
    ],
    cierre: "Your warehouse and your numbers, in the same place.",
  },
  {
    slug: "services",
    frase: "An agency’s hours, projects and invoices.",
    nombre: "Services",
    tipo: PAGINAS_DE_SECTOR[3].tipo,
    modulos: PAGINAS_DE_SECTOR[3].modulos,
    metaTitulo: "Software for agencies and advisory firms: hours, projects and invoices · Zindra",
    metaDescripcion: "Clock-in and remote work, projects, chat with agents and invoicing for agencies, advisory firms, consultancies and studios.",
    antetitulo: "FOR AGENCIES, ADVISORY FIRMS, CONSULTANCIES AND STUDIOS",
    titular: "Your team’s hours, projects and invoices, in one place.",
    entradilla: "Who’s working on what, how long it takes and who gets billed. With chat and projects in the same app as clock-in.",
    llamada: "Set up Zindra for my agency",
    segunda: { texto: "I’m coming from Asana", href: "/en/work" },
    diaTitulo: "A week in your agency",
    dia: [
      { hora: "Monday 09:00", titulo: "The week gets planned", texto: "Each project with its tasks, owners and dates, on a board or a calendar.", modulos: ["proyectos"] },
      { hora: "Every day", titulo: "The team clocks in from their computer", texto: "At the office or remote, from Zindra Work. The working-hours record takes care of itself.", modulos: ["fichaje", "teletrabajo"] },
      { hora: "Any time", titulo: "You talk where you work", texto: "A channel per client or per project, with threads and mentions.", modulos: ["chat"] },
      { hora: "Month end", titulo: "Invoicing", texto: "Invoices and quotes with a link for the client; payments reconciled with the bank.", modulos: ["facturacion", "banca"] },
      { hora: "Whenever you like", titulo: "You ask how things are going", texto: "“How many hours has the team logged this month?” The agent answers with your data and your permissions.", modulos: ["informes", "chat"] },
    ],
    ejemplo: { titulo: "A 15-person agency", personas: PAGINAS_DE_SECTOR[3].ejemplo.personas },
    especial: {
      etiqueta: "COMING FROM ASANA",
      titulo: "Your projects come over on their own",
      texto: "Connect Asana, match the people, and projects, tasks, attachments and comments come in with their status. New work keeps arriving while you use both.",
      llamada: "See Zindra Work",
      href: "/en/work",
    },
    integracionesTitulo: "What you already use",
    integraciones: [
      { nombre: "Asana", texto: "Projects and tasks, with columns and statuses." },
      { nombre: "Google Calendar", texto: "Everyone’s calendar next to the team’s." },
      { nombre: "Holded", texto: "Clients, invoices and purchases, imported into Invoicing." },
    ],
    matices: [
      { titulo: "Clients, not tables", texto: "You only see what you use: clients and projects, no stock or dining rooms." },
      { titulo: "Remote work", texto: "Clock-in from the computer and, if you turn it on, real time while remote." },
      { titulo: "A channel per client", texto: "Chat is organised the way you work, with the project alongside." },
    ],
    preguntas: [
      { pregunta: "Can I use just Chat and Projects?", respuesta: "Yes: the core with Chat and Projects, paid per person. Everything else, when you need it." },
      { pregunta: "Does it record remote work?", respuesta: "Yes. Clock-in for working hours and Remote work for activity while remote." },
      { pregunta: "Do my clients see anything?", respuesta: "Invoices and quotes have their own link for the client." },
    ],
    cierre: "Your agency, set up in an afternoon.",
  },
  {
    slug: "software",
    frase: "A remote team, with its hours and its projects.",
    nombre: "Software",
    tipo: PAGINAS_DE_SECTOR[4].tipo,
    modulos: PAGINAS_DE_SECTOR[4].modulos,
    metaTitulo: "For software companies and remote teams · Zindra",
    metaDescripcion: "Clock-in and remote work from the desktop, projects and chat in the same app, and payroll and invoices. For remote teams.",
    antetitulo: "FOR SOFTWARE COMPANIES AND REMOTE TEAMS",
    titular: "A remote team, with its hours, its projects and its chat.",
    entradilla: "Clock-in and remote work from the desktop, projects and chat in the same app, and everyone’s payslips. Instead of five tools that don’t talk to each other.",
    llamada: "Set up Zindra for my team",
    segunda: { texto: "Discover Zindra Work", href: "/en/work" },
    diaTitulo: "A week with your team",
    dia: [
      { hora: "09:00", titulo: "Everyone starts from home", texto: "They clock in from the desktop app; while clocked in remotely, their activity is recorded.", modulos: ["fichaje", "teletrabajo"] },
      { hora: "09:15", titulo: "Daily in the team channel", texto: "Threads, mentions and calls with notes, inside the chat.", modulos: ["chat"] },
      { hora: "All week", titulo: "Sprint on the board", texto: "Projects with tasks and subtasks, custom columns and a delivery calendar.", modulos: ["proyectos"] },
      { hora: "When needed", titulo: "Time off without emails", texto: "Requested in the app, approved, and shown on the team calendar.", modulos: ["ausencias"] },
      { hora: "Month end", titulo: "Payroll and invoices", texto: "Everyone gets their payslip in the app; client invoices come out of Invoicing.", modulos: ["nominas", "facturacion"] },
    ],
    ejemplo: { titulo: "A 20-person team", personas: PAGINAS_DE_SECTOR[4].ejemplo.personas },
    especial: {
      etiqueta: "ON THE DESKTOP",
      titulo: "Zindra Work for Mac and Windows",
      texto: "Chat, projects and the clock-in button always at hand, with notifications. And, if you turn it on, remote work tracking while clocked in.",
      llamada: "Discover Zindra Work",
      href: "/en/work",
    },
    integracionesTitulo: "What you already use",
    integraciones: [
      { nombre: "Asana", texto: "Projects and tasks, with their status." },
      { nombre: "Google Calendar", texto: "The team’s calendars and meetings." },
      { nombre: "Factorial", texto: "Your staff list, so you don’t add everyone by hand." },
    ],
    matices: [
      { titulo: "Remote work with limits", texto: "Activity is only recorded while the person is clocked in remotely." },
      { titulo: "Agents with permissions", texto: "The agent only sees what the person asking can see." },
      { titulo: "No shifts or tills", texto: "You don’t see what you don’t use." },
    ],
    preguntas: [
      { pregunta: "What does Remote work record?", respuesta: "Time, apps and websites while the person is clocked in remotely. Outside working hours, nothing." },
      { pregunta: "Can I bring my projects over from Asana?", respuesta: "Yes, with their tasks, attachments, comments and statuses." },
      { pregunta: "Where does it work?", respuesta: "In the browser, on Mac and Windows, and on iPhone and Android." },
    ],
    cierre: "Your team, in a single app.",
  },
  {
    slug: "coffee-roasters",
    frase: "From green beans to the bag sold.",
    nombre: "Coffee roasters",
    tipo: PAGINAS_DE_SECTOR[5].tipo,
    modulos: PAGINAS_DE_SECTOR[5].modulos,
    conTostador: PAGINAS_DE_SECTOR[5].conTostador,
    metaTitulo: "Software for coffee roasters, integrated with Shopify · Zindra",
    metaDescripcion: "Green coffee varieties with their origin and cupping, roast curves, packing with its cost and sales on Shopify. From green beans to the bag sold.",
    antetitulo: "FOR SPECIALTY COFFEE ROASTERS",
    titular: "From green beans to the bag sold, in one place.",
    entradilla: "Each variety with its origin, process and cupping; each roast with its curve; each bag with its cost. And sales on Shopify, with stock up to date.",
    llamada: "Talk about the Roaster",
    segunda: { texto: "See the modules", href: "/en/modules" },
    diaTitulo: "A day at the roastery",
    dia: [
      { hora: "Green arrives", titulo: "The sack comes in with its record", texto: "Origin, process and cupping for each variety, with what it cost to bring it here.", modulos: ["tostador", "compras"] },
      { hora: "Roasting", titulo: "The roaster sends its curve", texto: "Zindra Roaster reads the Diedrich over USB or the network and saves the curve of each batch. From other roasters, the curve is uploaded as a CSV.", modulos: ["tostador"] },
      { hora: "Packing", titulo: "From batch to bags", texto: "The roasted coffee is packed into formats and goes into stock with its cost.", modulos: ["producciones", "inventario"] },
      { hora: "Sales", titulo: "Shopify and wholesale", texto: "Online orders take stock off; wholesale customers, with their invoice.", modulos: ["inventario", "facturacion"] },
      { hora: "Every month", titulo: "What each coffee earns", texto: "From the sack of green to the bag sold, the margin of each coffee.", modulos: ["informes"] },
    ],
    ejemplo: { titulo: "An 8-person roastery", personas: PAGINAS_DE_SECTOR[5].ejemplo.personas },
    especial: {
      etiqueta: "ZINDRA ROASTER",
      titulo: "Your roaster talks to Zindra",
      texto: "A small app on the roastery computer that reads the roaster through its USB cable or the warehouse network and sends Zindra the curve of each batch. Today it reads Diedrich machines; from other roasters, the curve is uploaded as a CSV.",
      llamada: "Talk about the Roaster",
      href: "/en/contact",
    },
    integracionesTitulo: "Your online store",
    integraciones: [
      { nombre: "Shopify", texto: "Orders and products, with bag stock up to date." },
      { nombre: "Your roaster", texto: "Diedrich machines over USB or the network with Zindra Roaster; others, with their curve as a CSV." },
      { nombre: "Your bank", texto: "To reconcile green coffee purchases and sales." },
    ],
    matices: [
      { titulo: "Varieties, not products", texto: "Each green coffee with its origin, process and cupping." },
      { titulo: "Roast curves", texto: "Each batch keeps its curve, so you can repeat what went well." },
      { titulo: "From sack to bag", texto: "The cost of each bag comes from its green coffee." },
    ],
    preguntas: [
      { pregunta: "Why doesn’t the Roaster have a price?", respuesta: "It’s turned on on request, because your roaster has to be connected. We give it to you when we talk." },
      { pregunta: "Can I use just Inventory and Shopify?", respuesta: "Yes. The Roaster is added whenever you like, on the same stock." },
      { pregunta: "Does it work if I also have a café?", respuesta: "Yes: turn on the hospitality modules for the same company." },
    ],
    cierre: "Your roastery, from sack to bag.",
  },
]
